import React, { useEffect, useRef } from 'react';
import { Pill as DesignSystemPill } from '@lmn/design-system';
import { PillCustomWrapper, PillHiddenLabel } from '@/app/common/components/Pill/styled.ts';
import { Label, LabelContainer, LabelCount } from '@/app/common/components/Pill/Label/styled.ts';
import ArrowIcon from '@/app/components/search/common/Icons/ArrowIcon';

type FilterPillsProps = {
  label?: string | React.ReactElement;
  icon?: React.ReactElement;
  handleClick?: (event: React.SyntheticEvent) => void;
  handleDelete?: (event: React.SyntheticEvent) => void;
  enabled?: boolean;
  /** Inert but still named, focusable and aria-disabled — a skeleton stays a control while it waits. */
  busy?: boolean;
  selected?: boolean;
  /** Wears the loading skeleton, keeping its name out of sight. */
  isLoading: boolean;
  isOpen: boolean;
  showArrow: boolean;
  arrowIcon?: React.ReactNode;
  filtersCount?: number;
  ref?: React.Ref<HTMLDivElement>;
  className?: string;
  keepArrowWhenSelected?: boolean;
  expanded?: boolean;
  pressed?: boolean;
  controls?: string;
};

export const DESIGN_SYSTEM_TRIGGER = '[data-role="button"]';

const setOrRemove = (node: Element, name: string, value: string | undefined) => {
  if (value === undefined) node.removeAttribute(name);
  else node.setAttribute(name, value);
};

const Pill = ({
  label,
  icon,
  handleClick,
  handleDelete,
  enabled,
  busy,
  selected,
  isLoading,
  isOpen,
  showArrow = false,
  arrowIcon,
  filtersCount,
  ref,
  className,
  keepArrowWhenSelected,
  expanded,
  pressed,
  controls,
}: FilterPillsProps) => {
  const isInteractive = enabled !== false;
  const inert = !!busy || isLoading;
  const skeleton = isLoading;

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const forwarded = ref as React.RefCallback<HTMLDivElement> | React.MutableRefObject<HTMLDivElement | null> | null;

  const attachRef = (node: HTMLDivElement | null) => {
    wrapperRef.current = node;
    if (typeof forwarded === 'function') forwarded(node);
    else if (forwarded) forwarded.current = node;
  };

  const clickable = isInteractive && !!handleClick;
  const whenIdle = (handler?: (event: React.SyntheticEvent) => void) =>
    handler && ((event: React.SyntheticEvent) => (inert ? undefined : handler(event)));

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const trigger = wrapper?.querySelector(DESIGN_SYSTEM_TRIGGER) ?? wrapper?.firstElementChild;
    if (!trigger) return undefined;

    setOrRemove(trigger, 'role', clickable ? 'button' : undefined);
    setOrRemove(trigger, 'aria-controls', clickable ? controls : undefined);
    setOrRemove(trigger, 'aria-expanded', clickable && expanded !== undefined ? String(expanded) : undefined);
    setOrRemove(trigger, 'aria-pressed', clickable && pressed !== undefined ? String(pressed) : undefined);
    setOrRemove(trigger, 'aria-disabled', clickable && inert ? 'true' : undefined);

    if (!clickable) return undefined;

    const keepSpaceFromScrolling = (event: Event) => {
      if ((event as KeyboardEvent).key === ' ') event.preventDefault();
    };
    trigger.addEventListener('keydown', keepSpaceFromScrolling);
    return () => trigger.removeEventListener('keydown', keepSpaceFromScrolling);
  }, [clickable, inert, controls, expanded, pressed]);

  const labelComponent = () => {
    const showArrowIcon = showArrow && !skeleton && (!selected || !!keepArrowWhenSelected);

    return (
      <LabelContainer isOpen={isOpen}>
        {!skeleton && (
          <>
            <Label>{label}</Label>
            {filtersCount ? <LabelCount>({filtersCount})</LabelCount> : ''}
          </>
        )}
        {skeleton && !!label && <PillHiddenLabel data-testid="pill-hidden-label">{label}</PillHiddenLabel>}
        {showArrowIcon && (arrowIcon ?? <ArrowIcon />)}
      </LabelContainer>
    );
  };

  return (
    <PillCustomWrapper
      $selected={selected}
      $enabled={inert ? false : enabled}
      $loading={skeleton}
      $open={isOpen}
      ref={attachRef}
      className={className}
      data-loading={skeleton || undefined}
    >
      <DesignSystemPill
        label={labelComponent()}
        icon={skeleton ? undefined : icon}
        handleClick={isInteractive ? whenIdle(handleClick) : undefined}
        handleDelete={isInteractive && !skeleton ? whenIdle(handleDelete) : undefined}
        enabled={enabled}
        selected={selected}
      />
    </PillCustomWrapper>
  );
};

export default Pill;
